import type { MessageDescriptor } from "react-intl";

import type { TPatient } from "~/types/patient";

import { CourseId } from "./courses";

// Memory
export const KB = 1024;
export const MB = KB * KB;
export const GB = KB * KB * KB;

// LIMITs
export const CBCT_MAX_FILESIZE = 1.5 * GB;
export const PATIENT_MAX_FILESIZE = 150 * MB;

// Time
export const SEC = 1000;
export const MIN = SEC * 60;
export const HOUR = MIN * 60;
export const DAY = HOUR * 24;
export const WEEK = DAY * 7;

export enum RxTypeId {
  STANDARD = 1,
}

export enum CommentType {
  COMMON = 0,
  CORRECTION = 1,
  REWORK = 2,
  APPROVE = 3,
  CE_QUESTION = 4,
  REWORK_PRESICE = 5,
  ORDER_RETAINERS = 6,
}

export const COMMENT_TYPE_OPTIONS: Partial<Record<CommentType, { color: string; translation: MessageDescriptor["id"] }>> = {
  [CommentType.REWORK]: {
    color: "orange",
    translation: "COMMENT_TYPE_REWORK",
  },
  [CommentType.CORRECTION]: {
    color: "orange",
    translation: "COMMENT_TYPE_CORRECTION",
  },
  [CommentType.CE_QUESTION]: {
    color: "green",
    translation: "COMMENT_TYPE_CE_QUESTION",
  },
  [CommentType.REWORK_PRESICE]: {
    color: "red",
    translation: "COMMENT_TYPE_REWORK_PRESICE",
  },
  [CommentType.ORDER_RETAINERS]: {
    color: "green",
    translation: "COMMENT_TYPE_ORDER_RETAINERS",
  },
};

export enum FileType {
  _3SHAPE = "3shape",
  DICOM = "dicom",
  PREXION = "prexion",
  MEDIT = "medit",
}

export enum Gender {
  FEMALE = 1,
  MALE = 2,
}

export const GENDER_MAP = {
  [Gender.FEMALE]: "SEX_F",
  [Gender.MALE]: "SEX_M",
} as const;

export enum PaymentOptionId {
  ADVANCED = 1,
  SPLIT_2 = 2,
  SPLIT_3 = 3,
  SPLIT_2_2 = 4,
  SPLIT_3_2 = 5,
}

export enum PaymentOption {
  ADVANCED = "PO_ADVANCE",
  SPLIT_2 = "PO_SPLIT_2",
  SPLIT_3 = "PO_SPLIT_3",
  SPLIT_2_2 = "PO_SPLIT_2_2",
  SPLIT_3_2 = "PO_SPLIT_3_2",
}

export enum PaymentMethodId {
  INVOICE = 1,
  /** @deprecated */
  CASH = 2,
  CARD = 3,
}

export enum PaymentMethod {
  BANK = "PM_BANK",
  /** @deprecated */
  CASH = "PM_CASH",
  CARD = "PM_CARD",
}

export const DECIMAL_PRECISION = 2;

export enum PayerId {
  CLINIC = 1,
  DOCTOR = 2,
  PATIENT = 3,
}

export const PAYERS_TRANSLATION_MAP = {
  [PayerId.CLINIC]: "PAYER_CLINIC",
  [PayerId.PATIENT]: "PAYER_PATIENT",
  [PayerId.DOCTOR]: "PAYER_DOCTOR",
} as const;

export const PAYMENT_METHODS_TRANSLATION_MAP: Record<PaymentMethodId, MessageDescriptor["id"]> = {
  [PaymentMethodId.INVOICE]: "PM_BANK",
  [PaymentMethodId.CASH]: "PM_CASH",
  [PaymentMethodId.CARD]: "PM_CARD",
};

export const LINK_TARGET_BLANK = "_blank";
export const LINK_TARGET_SELF = "_self";

export const PAYMENT_OPTIONS_TRANSLATION_MAP: Record<PaymentOptionId, MessageDescriptor["id"]> = {
  [PaymentOptionId.ADVANCED]: "PO_ADVANCE",
  [PaymentOptionId.SPLIT_2]: "PO_SPLIT_2",
  [PaymentOptionId.SPLIT_2_2]: "PO_SPLIT_2_2",
  [PaymentOptionId.SPLIT_3]: "PO_SPLIT_3",
  [PaymentOptionId.SPLIT_3_2]: "PO_SPLIT_3_2",
} as const;

export enum SOMaterial {
  LAST_STAGE = 1,
  USE_SCAN = 2,
  USE_IMPRESSIONS = 3,
}

export enum SOArch {
  BOTH = 1,
  UPPER = 2,
  LOWER = 3,
}

export const EXTRA_SERVICE_OPTION_TRANSLATION_MAP = {
  [SOMaterial.LAST_STAGE]: "EXTRA_SERVICE_OPTION_LAST_STAGE",
  [SOMaterial.USE_SCAN]: "EXTRA_SERVICE_OPTION_USE_SCAN",
  [SOMaterial.USE_IMPRESSIONS]: "EXTRA_SERVICE_OPTION_USE_IMPRESSIONS",
} as const;

export const ARCHES = {
  1: "TA_BOTH",
  2: "TA_UPPER",
  3: "TA_LOWER",
} as const;

export enum StatusId {
  UNFILLED_CASE = 0,
  INSTRUCTIONS_PROCESSING = 1,
  RETAKE_IMPRESSIONS = 2,
  WAITING_FOR_PAYMENT = 3,
  SCANNING_3D = 4,
  MODELING_3D = 5,
  PLAN_APPROVAL = 6,
  PLAN_3D_MODELS_BUILDING = 7,
  PRODUCTION_PRINTING = 8,
  PRODUCTION_FABRICATING = 9,
  FINAL_STAGE_READY = 10,
  TREATMENT_FINISHED = 11,
  TREATMENT_CANCELED = 12,
  RETAINERS_ORDERED = 13,
  RETAINERS_PRINTING = 14,
  RETAINERS_FABRICATING = 15,
  RETAINERS_READY = 16,
  RETAINERS_SCANNING = 17,
  PRODUCTION_MOLDING = 18,
  PRODUCTION_MARKING = 19,
  PRODUCTION_ALLOCATING = 20,
  PRODUCTION_QUALITY_CONTROL = 21,
  RETAINERS_RETAKE_IMPRESSIONS = 22,
  RETAINERS_WAITING_FOR_PAYMENT = 23,
  POWER_CAP_ORDERED = 24,
  POWER_CAP_RETAKE_IMPRESSIONS = 25,
  POWER_CAP_WAITING_FOR_PAYMENT = 26,
  POWER_CAP_SCANNING_3D = 27,
  POWER_CAP_MODELING_3D = 28,
  POWER_CAP_PLAN_APPROVAL = 29,
  POWER_CAP_PLAN_3D_MODELS_BUILDING = 30,
  POWER_CAP_PRODUCTION_PRINTING = 31,
  POWER_CAP_PRODUCTION_MOLDING = 32,
  POWER_CAP_PRODUCTION_MARKING = 33,
  POWER_CAP_PRODUCTION_ALLOCATING = 34,
  POWER_CAP_PRODUCTION_FABRICATING = 35,
  POWER_CAP_PRODUCTION_QUALITY_CONTROL = 36,
  POWER_CAP_READY = 37,
}

export const STATUSES: { value: StatusId; label: TPatient["status"] }[] = [
  { value: StatusId.UNFILLED_CASE, label: "S_UNFILLED_CASE" },
  { value: StatusId.INSTRUCTIONS_PROCESSING, label: "S_INSTRUCTIONS_PROCESSING" },
  { value: StatusId.RETAKE_IMPRESSIONS, label: "S_RETAKE_IMPRESSIONS" },
  { value: StatusId.WAITING_FOR_PAYMENT, label: "S_WAITING_FOR_A_PAYMENT" },
  { value: StatusId.SCANNING_3D, label: "S_3D_SCANNING" },
  { value: StatusId.MODELING_3D, label: "S_3D_PLAN_MODELING" },
  { value: StatusId.PLAN_APPROVAL, label: "S_3D_PLAN_APPROVAL" },
  { value: StatusId.PRODUCTION_PRINTING, label: "S_PRODUCTION_PRINTING" },
  { value: StatusId.PRODUCTION_MOLDING, label: "S_PRODUCTION_MOLDING" },
  { value: StatusId.PRODUCTION_MARKING, label: "S_PRODUCTION_MARKING" },
  { value: StatusId.PRODUCTION_FABRICATING, label: "S_PRODUCTION_FABRICATING" },
  { value: StatusId.PRODUCTION_QUALITY_CONTROL, label: "S_PRODUCTION_QUALITY_CONTROL" },
  { value: StatusId.FINAL_STAGE_READY, label: "S_FINAL_STAGE_READY" },
  { value: StatusId.TREATMENT_FINISHED, label: "S_TREATMENT_FINISHED" },
  { value: StatusId.TREATMENT_CANCELED, label: "S_CANCEL_TREATMENT" },
  { value: StatusId.RETAINERS_ORDERED, label: "S_RETAINERS_ORDERED" },
  { value: StatusId.RETAINERS_RETAKE_IMPRESSIONS, label: "S_RETAINERS_RETAKE_IMPRESSIONS" },
  { value: StatusId.RETAINERS_WAITING_FOR_PAYMENT, label: "S_RETAINERS_WAITING_FOR_A_PAYMENT" },
  { value: StatusId.RETAINERS_SCANNING, label: "S_RETAINERS_3D_SCANNING" },
  { value: StatusId.RETAINERS_PRINTING, label: "S_RETAINERS_PRINTING" },
  { value: StatusId.RETAINERS_FABRICATING, label: "S_RETAINERS_FABRICATING" },
  { value: StatusId.RETAINERS_READY, label: "S_RETAINERS_READY" },
  { value: StatusId.POWER_CAP_ORDERED, label: "S_POWER_CAP_ORDERED" },
  { value: StatusId.POWER_CAP_RETAKE_IMPRESSIONS, label: "S_POWER_CAP_RETAKE_IMPRESSIONS" },
  { value: StatusId.POWER_CAP_WAITING_FOR_PAYMENT, label: "S_POWER_CAP_WAITING_FOR_A_PAYMENT" },
  { value: StatusId.POWER_CAP_SCANNING_3D, label: "S_POWER_CAP_3D_SCANNING" },
  { value: StatusId.POWER_CAP_MODELING_3D, label: "S_POWER_CAP_3D_PLAN_MODELING" },
  { value: StatusId.POWER_CAP_PLAN_APPROVAL, label: "S_POWER_CAP_3D_PLAN_APPROVAL" },
  { value: StatusId.POWER_CAP_PRODUCTION_PRINTING, label: "S_POWER_CAP_PRODUCTION_PRINTING" },
  { value: StatusId.POWER_CAP_PRODUCTION_MOLDING, label: "S_POWER_CAP_PRODUCTION_MOLDING" },
  { value: StatusId.POWER_CAP_PRODUCTION_MARKING, label: "S_POWER_CAP_PRODUCTION_MARKING" },
  { value: StatusId.POWER_CAP_PRODUCTION_FABRICATING, label: "S_POWER_CAP_PRODUCTION_FABRICATING" },
  { value: StatusId.POWER_CAP_PRODUCTION_QUALITY_CONTROL, label: "S_POWER_CAP_PRODUCTION_QUALITY_CONTROL" },
  { value: StatusId.POWER_CAP_READY, label: "S_POWER_CAP_READY" },
];

export enum PaymentSystem {
  SBERBANK = "sberbank",
  YOOKASSA = "yookassa",
}

export const PAYMENT_SYSTEM_TYPE: PaymentSystem = PaymentSystem.YOOKASSA;

export const SBERBANK_DISCLAIMER = `Для оплаты (ввода реквизитов Вашей карты) Вы будете перенаправлены на платежный шлюз ПАО СБЕРБАНК. Соединение
с платежным шлюзом и передача информации осуществляется в защищенном режиме с использованием протокола
шифрования SSL. В случае если Ваш банк поддерживает технологию безопасного проведения интернет-платежей Verified
By Visa или MasterCard SecureCode для проведения платежа также может потребоваться ввод специального пароля.
Настоящий сайт поддерживает 256-битное шифрование. Конфиденциальность сообщаемой персональной информации
обеспечивается ПАО СБЕРБАНК. Введенная информация не будет предоставлена третьим лицам за исключением случаев,
предусмотренных законодательством РФ. Проведение платежей по банковским картам осуществляется в строгом
соответствии с требованиями платежных систем МИР, Visa Int. и MasterCard Europe Sprl.`;

// Common

export enum ZIndex {
  /** FAB; drawer overlay +1, drawer/popover surface +2 — must sit above `.page-header` (~9995). */
  SUPPORT_WIDGET = 10040,
  TOASTER = 10100,
  CENTERED_DIALOG = 10110,
}

export const SUPPORT_PHONE_NUMBER = "+74952114454";

export const COURSES_TIPS: Partial<Record<CourseId, string[]>> = {
  [CourseId.LIMITLESS]: [
    "2 класс  - дистализация более 2.5 мм",
    "3 класс - дистализация более 2 мм ",
    "скученность 3 степени и более (если для ее устранения требуется дистализация, превышающая 2 мм)",
    "раскрытие места под отсутствующий зуб (если требуется дистализация/мезиализация более 2,5 мм)",
    "сочетанные патологии, например: открытый прикус/глубокий + перекрест и т.д",
    "перекрест двух и более зубов, с коррекцией по классу и скученностью 2-3 степени",
    "хирургические случаи - патологии, требующие зубоальвеолярной компенсации скелетных несоответствий (если это не подготовка под хирургию)",
    "смещение средней линии более 2,5 мм",
    "лечение с удалением премоляров (закрытие постэкстракционных промежутков)",
  ],
  [CourseId.FULL]: [
    "2 класс, с дистализацией до 2,5 мм",
    "3 класс, с дистализацией до 2 мм",
    "скученность 2 степени",
    "глубокий прикус",
    "открытый прикус",
    "бипротрузия (если дистализация до 2 мм в каждом сегменте)",
    "закрытие трем (до 3-4 мм)",
  ],
  [CourseId.SHORT]: ["скученность 1-2 степени", "закрытие трем до 2 мм", "ротация резцов/клыков ≤ 28°, премоляров - ≤ 28°, моляров - ≤ 10°"],
  [CourseId.SUPER_SHORT]: ["без изменения класса", "скученность ≤ 1мм", "промежутки ≤ 1мм", "без смещения средней линии", "ротация ≤ 15°"],
  [CourseId.SINGLE_ARCH]: [
    "изменение класса ≤ 2,5мм",
    "скученность ≤ 5мм",
    "промежутки ≤ 5мм (ориентир антагонисты)",
    "смещение средней линии ≥ 2мм",
    "ротация резцов/клыков ≥ 30°, премоляров/моляров ≥ 20°",
  ],
};
